// API functions for address management

// Get saved addresses
export const getSavedAddressesAPI = async ({ userId, cloudFunctionsUrl }) => {
  try {
    const response = await fetch(`${cloudFunctionsUrl}/getDeliveryAddresses`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch addresses. Status: ${response.status}`);
    }

    const apiData = await response.json();

    // Transform API response to match our component expectations
    const transformedData = {
      data: {
        addresses:
          apiData.data
            ?.map((address) => ({
              id: address.id,
              placeId: address.placeId || '',
              label: address.name || 'Address', // Use name field or default label
              addressLine1: address.address || '',
              addressLine2: address.area || address.city || '',
              latitude: address.location?.lat || null,
              longitude: address.location?.lng || null,
              apartment: address.apartment || '',
              entryCode: address.entryCode || '',
              buildingName: address.buildingName || '',
              dropOffOption: address.dropOffOption || '',
              notes: address.dropOffNotes || '',
              isDefault: address.isDefault || false,
              userId: address.userId,
              areaCode: address.areaCode || '',
              additionalInformation: address.additionalInformation || null,
              // Keep original API fields for reference
              _original: address,
            }))
            ?.filter((address) => address.placeId) || [],
      },
    };

    return transformedData;
  } catch (error) {
    console.error('Error fetching saved addresses:', error);
    throw error;
  }
};

// Add new address
export const addAddressAPI = async ({
  userId,
  addressData,
  cloudFunctionsUrl,
}) => {
  try {
    // Transform our form data to match API expectations
    const apiPayload = {
      userId,
      placeId: addressData.placeId,
      name: addressData.label || '',
      address: addressData.addressLine1 || '',
      area: addressData.addressLine2 || '', // Use full addressLine2 as area for now
      city: '', // Not in our form, keeping empty
      areaCode: addressData.areaCode || '',
      location: {
        lat: addressData.latitude,
        lng: addressData.longitude,
      },
      buildingName: addressData.buildingName || '',
      apartment: addressData.apartment || '',
      entryCode: addressData.entryCode || '',
      landmark: '', // Not in our form, keeping empty
      dropOffNotes: addressData.notes || '',
      dropOffOption: addressData.dropOffOption || '',
      isDefault: addressData.isDefault || false,
      ...(addressData.additionalInformation && {
        additionalInformation: addressData.additionalInformation,
      }),
    };

    const response = await fetch(`${cloudFunctionsUrl}/addDeliveryAddress`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(apiPayload),
    });

    let data;
    try {
      data = await response.json();
    } catch (jsonError) {
      data = null;
    }

    if (!response.ok) {
      // Try to extract a detailed error message from the response body
      const errorMsg =
        (data && (data.error || data.message)) ||
        `Failed to add address. Status: ${response.status}`;
      throw new Error(errorMsg);
    }

    return data;
  } catch (error) {
    console.error('Error adding address:', error);
    throw error;
  }
};

// Edit existing address
export const editAddressAPI = async ({
  userId,
  addressId,
  addressData,
  cloudFunctionsUrl,
}) => {
  try {
    // Transform our form data to match API expectations
    const apiPayload = {
      userId,
      addressId,
      placeId: addressData.placeId,
      name: addressData.label || '',
      address: addressData.addressLine1 || '',
      area: addressData.addressLine2 || '', // Use full addressLine2 as area for now
      city: addressData.city || '', // Not in our form, keeping empty
      areaCode: addressData.areaCode || '',
      location: {
        lat: addressData.latitude,
        lng: addressData.longitude,
      },
      buildingName: addressData.buildingName || '',
      apartment: addressData.apartment || '',
      entryCode: addressData.entryCode || '',
      landmark: '', // Not in our form, keeping empty
      dropOffNotes: addressData.notes || '',
      dropOffOption: addressData.dropOffOption || '',
      isDefault: addressData.isDefault || false,
      ...(addressData.additionalInformation && {
        additionalInformation: addressData.additionalInformation,
      }),
    };

    const response = await fetch(`${cloudFunctionsUrl}/updateDeliveryAddress`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(apiPayload),
    });

    let data;
    try {
      data = await response.json();
    } catch (jsonError) {
      data = null;
    }

    if (!response.ok) {
      // Try to extract a detailed error message from the response body
      const errorMsg =
        (data && (data.error || data.message)) ||
        `Failed to update address. Status: ${response.status}`;
      throw new Error(errorMsg);
    }

    return data;
  } catch (error) {
    console.error('Error editing address:', error);
    throw error;
  }
};

// Delete address
export const deleteAddressAPI = async ({
  userId,
  addressId,
  cloudFunctionsUrl,
}) => {
  try {
    const response = await fetch(`${cloudFunctionsUrl}/deleteDeliveryAddress`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        addressId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete address. Status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting address:', error);
    throw error;
  }
};

export const reverseGeocodeAPI = async ({
  latitude,
  longitude,
  storeConfig,
}) => {
  try {
    if (!latitude || !longitude || !storeConfig.cloudFunctionsUrl) {
      return null;
    }

    const response = await fetch(
      `${storeConfig.cloudFunctionsUrl}/getAddressByLocation`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lat: latitude,
          lng: longitude,
          result_type: 'street_address',
          country: storeConfig?.countryIdentifier,
          requestFrom: 'website',
          restaurantId: storeConfig?.restaurantId,
          storeId: storeConfig?.storeId,
        }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch reverse geocode data');
    }

    const res = await response.json();

    return res;
  } catch (error) {
    console.error('Error in reverseGeocodeAPI:', error);
    throw error;
  }
};
